import { Ref, ref, onUnmounted, MaybeRef, unref } from 'vue';
import { BulkSaveColumnWidthsUseCase } from '../BaseTableContent/useCases/bulkSaveColumnWidthsUseCase';
import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../BaseTableContent/constants';
import { TableType } from '@/coreDomainsModule/table';

/**
 * VueコンポーネントrefやHTMLElementから、実際のDOM要素を取得する
 * @param el - refで渡された値
 * @returns HTMLElement | null
 */
function getDomElement(el: any): HTMLElement | null {
  let current = el;
  // Vueコンポーネントの場合は$elをたどる
  while (current && !(current instanceof HTMLElement)) {
    current = current.$el;
  }
  return current instanceof HTMLElement ? current : null;
}

/**
 * テーブル列幅のリサイザーを管理するcomposable
 * @param columnResizerItem - useColumnResizerの初期化パラメータ
 * @returns リサイザーmousedown用ハンドラ
 */
export const useColumnResizer = (columnResizerItem: ColumnResizerItem) => {
  // --- 内部状態 ---
  const state: Ref<{
    startX: number;
    startWidth: number;
    targetKey: string;
  }> = ref({
    startX: 0,
    startWidth: 0,
    targetKey: '',
  });
  const localWidths = ref<Record<string, number>>({});
  let cachedHeader: HTMLElement | null = null;
  let ghostLineEl: HTMLDivElement | null = null;
  let ghostLineTable: HTMLElement | null = null;
  const isGhostLineVisible = ref(false);

  /**
   * ゴーストラインをテーブル内に表示する
   * @param x - left座標(px, テーブル内相対)
   */
  const showGhostLine = (x: number) => {
    const tableEl = getDomElement(columnResizerItem.tableEl.value);
    if (!tableEl) return;
    // ghostLineElがnull、または親ノードがtableElでなければ再生成
    if (!ghostLineEl || ghostLineEl.parentNode !== tableEl) {
      if (ghostLineEl && ghostLineEl.parentNode) {
        ghostLineEl.parentNode.removeChild(ghostLineEl);
      }
      ghostLineEl = document.createElement('div');
      ghostLineEl.className = 'ghost-column-resizer-line';
      // 視認性を最大化するためインラインスタイルを強制的に適用
      ghostLineEl.style.position = 'absolute';
      ghostLineEl.style.top = '0';
      ghostLineEl.style.bottom = '0';
      ghostLineEl.style.width = '3px';
      ghostLineEl.style.background = '#bdbdbd';
      ghostLineEl.style.opacity = '0.8';
      ghostLineEl.style.zIndex = '9999';
      ghostLineEl.style.pointerEvents = 'none';
      ghostLineEl.style.borderRadius = '2px';
      ghostLineEl.style.boxShadow = '0 0 8px #bdbdbd';
      ghostLineEl.style.display = 'block';
      tableEl.appendChild(ghostLineEl);
    }
    ghostLineTable = tableEl;
    ghostLineEl.style.left = `${x}px`;
    ghostLineEl.style.display = 'block';
    isGhostLineVisible.value = true;
  };

  /**
   * ゴーストラインを非表示にする
   */
  const hideGhostLine = () => {
    if (ghostLineEl) ghostLineEl.style.display = 'none';
    isGhostLineVisible.value = false;
  };

  onUnmounted(() => {
    if (ghostLineEl && ghostLineEl.parentNode) ghostLineEl.parentNode.removeChild(ghostLineEl);
    ghostLineEl = null;
    ghostLineTable = null;
  });

  /**
   * リサイザーにマウスダウンした際のイベント
   * @param event - マウスイベント
   * @param key - ヘッダーキー
   */
  const onMousedownResizer = (event: MouseEvent, key: string) => {
    state.value.targetKey = key;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    cachedHeader = target.closest(columnResizerItem.headerClassName ?? '.table__header') as HTMLElement | null;
    const header = cachedHeader;
    if (!header) return;
    const tableEl = getDomElement(columnResizerItem.tableEl.value);
    if (!tableEl) return;
    state.value.startWidth = header.clientWidth;
    state.value.startX = event.pageX;
    document.body.style.pointerEvents = 'none';
    const headerRect = header.getBoundingClientRect();
    const tableRect = tableEl.getBoundingClientRect();
    // ゴーストラインのleft座標（ヘッダー左端＋startWidth、最大値はscrollWidth）
    let leftInTable = headerRect.left - tableRect.left + tableEl.scrollLeft + state.value.startWidth;
    leftInTable = Math.max(0, Math.min(leftInTable, tableEl.scrollWidth));
    showGhostLine(leftInTable);
    document.addEventListener('mousemove', _onMoveResizer);
    document.addEventListener('mouseup', _onMouseUpResizer);
  };

  /**
   * リサイザーを動かした際のイベント
   * @param event - マウスイベント
   */
  const _onMoveResizer = (event: MouseEvent) => {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
    }
    if (!cachedHeader || !ghostLineTable) return;
    const tableEl = ghostLineTable;
    const diffX = event.pageX - state.value.startX;
    const headerRect = cachedHeader.getBoundingClientRect();
    const tableRect = tableEl.getBoundingClientRect();
    // 新しい幅を計算（最小・最大幅を考慮）
    const newWidth = Math.max(
      Math.min(state.value.startWidth + diffX, columnResizerItem.option?.maxWidth ?? MAX_COLUMN_WIDTH),
      MIN_COLUMN_WIDTH
    );
    // ゴーストラインのleft座標（ヘッダー左端＋newWidth、最大値はscrollWidth）
    let leftInTable = headerRect.left - tableRect.left + tableEl.scrollLeft + newWidth;
    leftInTable = Math.max(0, Math.min(leftInTable, tableEl.scrollWidth));
    ghostLineEl && (ghostLineEl.style.left = `${leftInTable}px`);
    ghostLineEl && (ghostLineEl.style.display = 'block');
    isGhostLineVisible.value = true;
  };

  /**
   * リサイザーからマウスを離した時のイベント
   * @param event - マウスイベント
   */
  const _onMouseUpResizer = async (event: MouseEvent) => {
    document.removeEventListener('mousemove', _onMoveResizer);
    document.removeEventListener('mouseup', _onMouseUpResizer);
    document.body.style.pointerEvents = '';
    hideGhostLine();
    if (cachedHeader && ghostLineTable) {
      const diffX = event.pageX - state.value.startX;
      const newWidth = Math.max(
        Math.min(state.value.startWidth + diffX, columnResizerItem.option?.maxWidth ?? MAX_COLUMN_WIDTH),
        MIN_COLUMN_WIDTH
      );
      columnResizerItem.widthInfo.value = {
        ...columnResizerItem.widthInfo.value,
        [state.value.targetKey]: newWidth,
      };
      const resultWidthInfo = Object.fromEntries(
        Object.entries(columnResizerItem.widthInfo.value).filter(([_, v]) => v !== undefined)
      ) as Record<string, number>;
      const bulkSaveColumnWidthsUseCase = new BulkSaveColumnWidthsUseCase();
      await bulkSaveColumnWidthsUseCase.Execute(unref(columnResizerItem.tableType), resultWidthInfo);
    }
    _resetState();
    localWidths.value = {};
    cachedHeader = null;
    ghostLineTable = null;
  };

  /**
   * Stateのリセット
   */
  const _resetState = () => {
    state.value = {
      startX: 0,
      startWidth: 0,
      targetKey: '',
    };
  };

  return {
    onMousedownResizer,
  };
};

/**
 * useColumnResizerの初期化パラメータ
 * @param tableType - テーブル種別
 * @param widthInfo - 列幅情報のRef
 * @param headerClassName - ヘッダー要素のクラス名
 * @param tableEl - テーブル要素のRef（HTMLElementまたはVueコンポーネントインスタンス）
 * @param option - オプション
 */
export type ColumnResizerItem = {
  tableType: MaybeRef<TableType>;
  widthInfo: Ref<Record<string, number | undefined>>;
  headerClassName?: string;
  tableEl: Ref<any>;
  option?: {
    maxWidth?: number;
  };
};
